import { useEffect, useRef, useState } from "react";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { enableViewCube, tiltView, setViewCubeFrame, renderViewCubeGizmo } from "@/lib/threeManual";
import { jsPDF } from "jspdf";
import { Ruler, Upload, Loader2, FileDown, Move3d, RotateCcw, SquareDashedMousePointer, X, Home, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";

// Build a clean technical line group: light-grey faces (hidden-line removal via
// depth) + black feature edges + inverted-hull silhouette for closed contours.
function buildTechnical(root, angle = 45) {
  root.updateMatrixWorld(true);
  const group = new THREE.Group();
  root.traverse((child) => {
    if (child.isMesh && child.geometry) {
      const g = child.geometry.clone().applyMatrix4(child.matrixWorld);
      const faceMat = new THREE.MeshBasicMaterial({ color: 0xe4e4e4 });
      faceMat.polygonOffset = true; faceMat.polygonOffsetFactor = 1; faceMat.polygonOffsetUnits = 1;
      const fm = new THREE.Mesh(g, faceMat); fm.userData.isFace = true; group.add(fm);
      group.add(new THREE.LineSegments(new THREE.EdgesGeometry(g, angle), new THREE.LineBasicMaterial({ color: 0x1b1b1b })));
      let og = g.clone();
      try { og = mergeVertices(og); } catch (e) { /* keep */ }
      og.computeVertexNormals(); og.computeBoundingSphere();
      const t = (og.boundingSphere?.radius || 1) * 0.006;
      const opos = og.attributes.position, onor = og.attributes.normal;
      for (let i = 0; i < opos.count; i++) opos.setXYZ(i, opos.getX(i) + onor.getX(i) * t, opos.getY(i) + onor.getY(i) * t, opos.getZ(i) + onor.getZ(i) * t);
      opos.needsUpdate = true;
      group.add(new THREE.Mesh(og, new THREE.MeshBasicMaterial({ color: 0x1b1b1b, side: THREE.BackSide })));
    }
  });
  return group;
}

const fmtMM = (v) => `${(Math.round(v * 10) / 10).toLocaleString("nl-NL", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;

// Detect centers of circular edge loops (holes, bores, cylinder rims) so the
// measurement tool can snap to a circle's center. Works on tessellated meshes:
// feature edges are welded into a graph, degree-2 loops are traced, and any loop
// whose points are ~equidistant from their centroid (low radius variance) is a circle.
function detectCircleCenters(root) {
  root.updateMatrixWorld(true);
  const centers = [];
  root.traverse((child) => {
    if (!child.isMesh || !child.geometry) return;
    const g = child.geometry.clone().applyMatrix4(child.matrixWorld);
    let edges;
    try { edges = new THREE.EdgesGeometry(g, 30); } catch (_) { return; }
    const pos = edges.attributes.position;
    const nodes = new Map();
    const key = (x, y, z) => `${Math.round(x * 1000)},${Math.round(y * 1000)},${Math.round(z * 1000)}`;
    const node = (x, y, z) => { const k = key(x, y, z); let n = nodes.get(k); if (!n) { n = { p: new THREE.Vector3(x, y, z), nbrs: new Set(), k }; nodes.set(k, n); } return n; };
    for (let i = 0; i < pos.count; i += 2) {
      const a = node(pos.getX(i), pos.getY(i), pos.getZ(i));
      const b = node(pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1));
      if (a === b) continue;
      a.nbrs.add(b.k); b.nbrs.add(a.k);
    }
    const visited = new Set();
    nodes.forEach((start) => {
      if (visited.has(start.k) || start.nbrs.size !== 2) return;
      const loop = []; let prevK = null, curK = start.k, ok = true;
      while (true) {
        const cur = nodes.get(curK);
        if (!cur || cur.nbrs.size !== 2) { ok = false; break; }
        visited.add(curK); loop.push(cur.p);
        const nb = [...cur.nbrs];
        const nextK = nb[0] === prevK ? nb[1] : nb[0];
        prevK = curK; curK = nextK;
        if (curK === start.k) break;
        if (loop.length > 20000) { ok = false; break; }
      }
      if (!ok || loop.length < 8) return;
      const c = new THREE.Vector3();
      loop.forEach((p) => c.add(p)); c.multiplyScalar(1 / loop.length);
      const radii = loop.map((p) => p.distanceTo(c));
      const mean = radii.reduce((s, r) => s + r, 0) / radii.length;
      if (mean < 1e-3) return;
      const cv = Math.sqrt(radii.reduce((s, r) => s + (r - mean) ** 2, 0) / radii.length) / mean;
      if (cv < 0.06) centers.push({ c: c.clone(), r: mean });
    });
  });
  return centers;
}

export default function TechnicalDrawing() {
  const mountRef = useRef(null);
  const engineRef = useRef(null);
  const groupRef = useRef(null);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const vertsRef = useRef([]);
  const circlesRef = useRef([]);
  const basisRef = useRef({ right: [1, 0, 0], up: [0, 1, 0], front: [0, 0, 1] });
  const downPos = useRef(null);
  const highlightRef = useRef([]);
  const magRef = useRef(null);

  const [hasModel, setHasModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dims, setDims] = useState(null); // {x,y,z} mm
  const [pdfUrl, setPdfUrl] = useState(null);
  const [faceMode, setFaceMode] = useState(false);
  const [frontN, setFrontN] = useState(null);
  const [oriented, setOriented] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [meta, setMeta] = useState({ project: "", part: "", drawingNo: "", date: today });
  const [logo, setLogo] = useState(null); // {url,w,h} uploaded PNG logo for the title block
  const [measureView, setMeasureView] = useState(null); // 'front'|'right'|'top'|null
  const [pendingPt, setPendingPt] = useState(null); // {u,v}
  const [measurements, setMeasurements] = useState([]); // {id,view,u1,v1,u2,v2,dist}
  const [, setTick] = useState(0); // force overlay redraw while measuring
  const cursorRef = useRef(null); // {clientX,clientY} while measuring

  useEffect(() => {
    const mount = mountRef.current;
    const w = mount.clientWidth, h = mount.clientHeight;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000000);
    camera.position.set(120, 90, 160);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    let raf;
    const animate = () => { raf = requestAnimationFrame(animate); if (!engineRef.current?.activeCam) controls.update(); renderer.render(scene, engineRef.current?.activeCam || camera); if (engineRef.current?._gizmo && !engineRef.current?.activeCam) renderViewCubeGizmo(engineRef.current); };
    animate();
    const onResize = () => {
      const nw = mount.clientWidth, nh = mount.clientHeight; if (!nw || !nh) return;
      const cur = renderer.getSize(new THREE.Vector2());
      if (Math.abs(cur.x - nw) < 1 && Math.abs(cur.y - nh) < 1) return; // skip no-op resizes
      renderer.setSize(nw, nh); camera.aspect = nw / nh; camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);
    let roRaf = 0;
    const ro = new ResizeObserver(() => { cancelAnimationFrame(roRaf); roRaf = requestAnimationFrame(onResize); }); // rAF-deferred to avoid "ResizeObserver loop" warnings
    ro.observe(mount);
    engineRef.current = { renderer, scene, camera, controls, syncSize: onResize };
    return () => {
      cancelAnimationFrame(raf); cancelAnimationFrame(roRaf); window.removeEventListener("resize", onResize); ro.disconnect();
      controls.dispose(); renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      engineRef.current = null;
    };
  }, []);

  const fitCamera = () => {
    const e = engineRef.current, g = groupRef.current; if (!e || !g) return;
    const box = new THREE.Box3().setFromObject(g);
    const c = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const r = Math.max(size.x, size.y, size.z) || 1;
    const d = r * 2.2;
    e.camera.position.set(c.x + d * 0.8, c.y + d * 0.6, c.z + d);
    e.camera.near = r / 1000; e.camera.far = r * 2000; e.camera.updateProjectionMatrix();
    e.controls.target.copy(c); e.controls.update();
  };

  // Orient the perspective camera to a named ViewCube face using the current basis.
  const namedDir = (name) => {
    const b = basisRef.current;
    const R = new THREE.Vector3(...b.right), U = new THREE.Vector3(...b.up), F = new THREE.Vector3(...b.front);
    switch (name) {
      case "front": return { d: F.clone(), up: U.clone() };
      case "back": return { d: F.clone().negate(), up: U.clone() };
      case "right": return { d: R.clone(), up: U.clone() };
      case "left": return { d: R.clone().negate(), up: U.clone() };
      case "top": return { d: U.clone(), up: F.clone().negate() };
      case "bottom": return { d: U.clone().negate(), up: F.clone() };
      default: return { d: F.clone().add(U.clone().multiplyScalar(0.8)).add(R.clone().multiplyScalar(0.8)), up: U.clone() };
    }
  };
  const setNamedView = (name) => {
    const e = engineRef.current, g = groupRef.current; if (!e || !g) return;
    const box = new THREE.Box3().setFromObject(g);
    const c = box.getCenter(new THREE.Vector3());
    const r = Math.max(...box.getSize(new THREE.Vector3()).toArray()) || 1;
    const { d, up } = namedDir(name); d.normalize(); up.normalize();
    e.camera.position.copy(c).addScaledVector(d, r * 2.2);
    e.camera.up.copy(up); e.controls.target.copy(c); e.camera.lookAt(c); e.controls.update();
  };
  const extentAlong = (axisArr) => {
    const ax = new THREE.Vector3(...axisArr).normalize();
    let mn = Infinity, mx = -Infinity;
    vertsRef.current.forEach((v) => { const d = v.dot(ax); if (d < mn) mn = d; if (d > mx) mx = d; });
    return mx - mn;
  };
  const computeDims = () => {
    const b = basisRef.current;
    setDims({ x: extentAlong(b.right), y: extentAlong(b.up), z: extentAlong(b.front) });
  };

  const addModel = (root, name) => {
    const e = engineRef.current;
    if (groupRef.current) { e.scene.remove(groupRef.current); groupRef.current = null; }
    root.updateMatrixWorld(true);
    // collect world-space vertices for exact extent measurement along any axis
    const verts = [];
    root.traverse((o) => {
      if (o.isMesh && o.geometry?.attributes?.position) {
        const p = o.geometry.attributes.position, mw = o.matrixWorld;
        for (let i = 0; i < p.count; i++) verts.push(new THREE.Vector3().fromBufferAttribute(p, i).applyMatrix4(mw));
      }
    });
    const g = buildTechnical(root, 45);
    const box = new THREE.Box3().setFromObject(g);
    const c = box.getCenter(new THREE.Vector3());
    g.position.sub(c);
    verts.forEach((v) => v.sub(c)); // center verts to match the centered group (origin frame)
    vertsRef.current = verts;
    circlesRef.current = detectCircleCenters(root).map((o) => ({ c: o.c.clone().sub(c), r: o.r }));
    e.scene.add(g);
    groupRef.current = g;
    // expose the fields the shared threeManual helpers (ViewCube, snap, dims) expect
    const bb0 = new THREE.Box3().setFromObject(g);
    e._box = bb0;
    e.radius = (Math.max(...bb0.getSize(new THREE.Vector3()).toArray()) || 1) / 2;
    e.parts = [{ id: "model", outer: g }];
    e.mount = mountRef.current;
    if (!e._gizmo) enableViewCube(e, (name) => setNamedView(name));
    setViewCubeFrame(e, {});
    basisRef.current = { right: [1, 0, 0], up: [0, 1, 0], front: [0, 0, 1] };
    setOriented(false); setFaceMode(false); setFrontN(null); clearHighlights();
    exitMeasure(); setMeasurements([]);
    computeDims();
    setMeta((m) => ({ ...m, part: m.part || name }));
    setPdfUrl(null);
    setHasModel(true); setLoading(false);
    fitCamera();
  };

  // View frame for a named ortho view (axes + projected bbox in view coords).
  const viewFrame = (name) => {
    const b = basisRef.current;
    const R = new THREE.Vector3(...b.right), U = new THREE.Vector3(...b.up), F = new THREE.Vector3(...b.front);
    let dir, up;
    if (name === "front") { dir = F.clone(); up = U.clone(); }
    else if (name === "right") { dir = R.clone(); up = U.clone(); }
    else if (name === "top") { dir = U.clone(); up = F.clone().negate(); }
    else { dir = F.clone().add(U.clone().multiplyScalar(0.8)).add(R.clone().multiplyScalar(0.8)); up = U.clone(); } // iso
    dir.normalize(); up.normalize();
    // The rendered ortho camera's vertical axis is perpendicular to dir; for the iso
    // view `up` (object-up) is NOT perpendicular to dir, so derive the true camera
    // basis here — otherwise the vertical extent is mis-measured and the model clips.
    const sr = new THREE.Vector3().crossVectors(up, dir).normalize();      // camera right
    const camUp = new THREE.Vector3().crossVectors(dir, sr).normalize();   // camera up (⊥ dir)
    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    vertsRef.current.forEach((v) => { const u = v.dot(sr), w = v.dot(camUp); if (u < uMin) uMin = u; if (u > uMax) uMax = u; if (w < vMin) vMin = w; if (w > vMax) vMax = w; });
    return { dir, up: camUp, sr, uMin, uMax, vMin, vMax };
  };

  // Render a named orthographic view, framed centered on the projected bbox.
  const renderOrtho = (name, longPx = 1200) => {
    const e = engineRef.current, g = groupRef.current;
    const { renderer, scene, camera: persp } = e;
    const size = new THREE.Box3().setFromObject(g).getSize(new THREE.Vector3());
    const fr = viewFrame(name);
    const wProj = (fr.uMax - fr.uMin) || 1, hProj = (fr.vMax - fr.vMin) || 1;
    const uMid = (fr.uMin + fr.uMax) / 2, vMid = (fr.vMin + fr.vMax) / 2;
    const target = fr.sr.clone().multiplyScalar(uMid).add(fr.up.clone().multiplyScalar(vMid));
    const m = 0.12;
    const halfW = (wProj / 2) * (1 + 2 * m), halfH = (hProj / 2) * (1 + 2 * m);
    const D = Math.max(size.x, size.y, size.z) * 3 + 10;
    const oc = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.01, D * 4);
    oc.position.copy(target).addScaledVector(fr.dir, D);
    oc.up.copy(fr.up); oc.lookAt(target); oc.updateProjectionMatrix();
    let pw, ph;
    if (wProj >= hProj) { pw = longPx; ph = Math.max(1, Math.round(longPx * hProj / wProj)); }
    else { ph = longPx; pw = Math.max(1, Math.round(longPx * wProj / hProj)); }
    const prevPR = renderer.getPixelRatio();
    const mount = mountRef.current;
    renderer.setPixelRatio(1); renderer.setClearColor(0xffffff, 1); renderer.setSize(pw, ph, false); renderer.render(scene, oc);
    const url = renderer.domElement.toDataURL("image/jpeg", 0.9);
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(prevPR); renderer.setSize(mount.clientWidth, mount.clientHeight);
    persp.aspect = mount.clientWidth / mount.clientHeight; persp.updateProjectionMatrix();
    return { url, wProj, hProj, uMin: fr.uMin, uMax: fr.uMax, vMin: fr.vMin, vMax: fr.vMax, name };
  };

  // --- measurement mode: pick 2 points on a locked ortho view ---
  const buildMeasureCam = (name) => {
    const e = engineRef.current, g = groupRef.current;
    const size = new THREE.Box3().setFromObject(g).getSize(new THREE.Vector3());
    const mount = mountRef.current;
    const ar = mount.clientWidth / mount.clientHeight;
    const fr = viewFrame(name);
    const wProj = (fr.uMax - fr.uMin) || 1, hProj = (fr.vMax - fr.vMin) || 1;
    const uMid = (fr.uMin + fr.uMax) / 2, vMid = (fr.vMin + fr.vMax) / 2;
    const target = fr.sr.clone().multiplyScalar(uMid).add(fr.up.clone().multiplyScalar(vMid));
    const m = 0.16;
    let halfW = (wProj / 2) * (1 + 2 * m), halfH = (hProj / 2) * (1 + 2 * m);
    if (halfW / halfH < ar) halfW = halfH * ar; else halfH = halfW / ar; // fit to canvas aspect
    const D = Math.max(size.x, size.y, size.z) * 3 + 10;
    const oc = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.01, D * 4);
    oc.position.copy(target).addScaledVector(fr.dir, D);
    oc.up.copy(fr.up); oc.lookAt(target); oc.updateProjectionMatrix();
    return { oc, fr };
  };
  const enterMeasure = (name) => {
    const e = engineRef.current; if (!e || !hasModel) return;
    e.syncSize && e.syncSize(); // ensure canvas matches current layout before building the ortho cam
    setFaceMode(false); setFrontN(null); clearHighlights(); setError("");
    const { oc, fr } = buildMeasureCam(name);
    e.activeCam = oc; e.measureFrame = fr; e.controls.enabled = false;
    setPendingPt(null); setMeasureView(name);
  };
  const exitMeasure = () => {
    const e = engineRef.current; if (e) { e.activeCam = null; e.controls.enabled = true; }
    cursorRef.current = null; setPendingPt(null); setMeasureView(null);
  };
  // nearest snappable point to a client point -> {x,y (client), v, kind} or null.
  // Circle/hole centers take priority over corner vertices.
  const snapScreen = (clientX, clientY) => {
    const e = engineRef.current, rect = mountRef.current.getBoundingClientRect();
    const cx = clientX - rect.left, cy = clientY - rect.top;
    const proj = (v) => { const p = v.clone().project(e.activeCam); return { sx: (p.x + 1) / 2 * rect.width, sy: (1 - p.y) / 2 * rect.height }; };
    // 1) circle centers (larger snap radius so they win near the middle of a hole)
    let best = null, bestD = 26;
    circlesRef.current.forEach((o) => {
      const { sx, sy } = proj(o.c);
      const d = Math.hypot(sx - cx, sy - cy);
      if (d < bestD) { bestD = d; best = { x: sx + rect.left, y: sy + rect.top, v: o.c, kind: "center" }; }
    });
    if (best) return best;
    // 2) corner vertices
    bestD = 22;
    vertsRef.current.forEach((v) => {
      const { sx, sy } = proj(v);
      const d = Math.hypot(sx - cx, sy - cy);
      if (d < bestD) { bestD = d; best = { x: sx + rect.left, y: sy + rect.top, v, kind: "vertex" }; }
    });
    return best;
  };
  const screenToUV = (ev) => {
    const e = engineRef.current, rect = mountRef.current.getBoundingClientRect(), fr = e.measureFrame;
    const snap = snapScreen(ev.clientX, ev.clientY);
    let pt;
    if (snap) pt = snap.v;
    else pt = new THREE.Vector3(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1, 0).unproject(e.activeCam);
    return { u: pt.dot(fr.sr), v: pt.dot(fr.up), snapped: !!snap };
  };
  const uvToScreen = (u, v) => {
    const e = engineRef.current, rect = mountRef.current.getBoundingClientRect(), fr = e.measureFrame;
    const P = fr.sr.clone().multiplyScalar(u).add(fr.up.clone().multiplyScalar(v));
    const p = P.project(e.activeCam);
    return { x: (p.x + 1) / 2 * rect.width, y: (1 - p.y) / 2 * rect.height };
  };
  const onMeasureClick = (ev) => {
    const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return;
    const uv = screenToUV(ev);
    if (!pendingPt) { setPendingPt(uv); }
    else {
      const du = uv.u - pendingPt.u, dv = uv.v - pendingPt.v;
      const dist = Math.hypot(du, dv);
      if (dist < 1e-3) { setError("De twee punten liggen te dicht bij elkaar"); return; }
      setMeasurements((arr) => [...arr, { id: Date.now(), view: measureView, u1: pendingPt.u, v1: pendingPt.v, u2: uv.u, v2: uv.v, dist }]);
      setPendingPt(null); setPdfUrl(null);
    }
  };
  const MAG_CSS = 132, MAG_PX = 264, MAG_ZOOM = 3.4;
  const drawMag = (clientX, clientY, snap) => {
    const mag = magRef.current, e = engineRef.current; if (!mag || !e) return;
    const src = e.renderer.domElement, rect = src.getBoundingClientRect();
    const sx = src.width / rect.width, sy = src.height / rect.height;
    const fX = snap ? snap.x : clientX, fY = snap ? snap.y : clientY;
    const bx = (fX - rect.left) * sx, by = (fY - rect.top) * sy;
    const srcBuf = MAG_PX / MAG_ZOOM;
    const ctx = mag.getContext("2d");
    ctx.clearRect(0, 0, MAG_PX, MAG_PX);
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, MAG_PX, MAG_PX);
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = "high";
    try { ctx.drawImage(src, bx - srcBuf / 2, by - srcBuf / 2, srcBuf, srcBuf, 0, 0, MAG_PX, MAG_PX); } catch (_) { /* best-effort magnifier; ignore out-of-bounds source rects */ }
    // faint grid crosshair
    ctx.strokeStyle = "rgba(20,20,20,0.35)"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(MAG_PX / 2, 0); ctx.lineTo(MAG_PX / 2, MAG_PX); ctx.moveTo(0, MAG_PX / 2); ctx.lineTo(MAG_PX, MAG_PX / 2); ctx.stroke();
    if (snap) {
      ctx.strokeStyle = "#DB960D"; ctx.lineWidth = 3.5;
      ctx.beginPath(); ctx.arc(MAG_PX / 2, MAG_PX / 2, snap.kind === "center" ? 16 : 12, 0, Math.PI * 2); ctx.stroke();
      if (snap.kind === "center") { // crosshair through the middle for hole/circle centers
        ctx.beginPath(); ctx.moveTo(MAG_PX / 2 - 22, MAG_PX / 2); ctx.lineTo(MAG_PX / 2 + 22, MAG_PX / 2); ctx.moveTo(MAG_PX / 2, MAG_PX / 2 - 22); ctx.lineTo(MAG_PX / 2, MAG_PX / 2 + 22); ctx.stroke(); }
    }
    else { ctx.fillStyle = "#DB960D"; ctx.beginPath(); ctx.arc(MAG_PX / 2, MAG_PX / 2, 4, 0, Math.PI * 2); ctx.fill(); }
  };
  const onMeasureMove = (ev) => {
    if (!measureView) return;
    const snap = snapScreen(ev.clientX, ev.clientY);
    cursorRef.current = { clientX: ev.clientX, clientY: ev.clientY, snap };
    drawMag(ev.clientX, ev.clientY, snap);
    setTick((t) => t + 1);
  };
  const onMeasureLeave = () => { if (measureView) { cursorRef.current = null; setTick((t) => t + 1); } };
  const removeMeasurement = (id) => { setMeasurements((arr) => arr.filter((m) => m.id !== id)); setPdfUrl(null); };

  useEffect(() => { const e = engineRef.current; if (e && e._gizmo) { const b = basisRef.current; setViewCubeFrame(e, { front: { d: b.front, up: b.up } }); } /* eslint-disable-next-line */ }, [oriented]);


  // --- face selection (front + bottom) to orient arbitrary models ---
  const clearHighlights = () => {
    const e = engineRef.current;
    if (e) highlightRef.current.forEach((h) => e.scene.remove(h));
    highlightRef.current = [];
  };
  const pickFaceLocal = (ev) => {
    const e = engineRef.current, rect = mountRef.current.getBoundingClientRect();
    const mouse = new THREE.Vector2(((ev.clientX - rect.left) / rect.width) * 2 - 1, -((ev.clientY - rect.top) / rect.height) * 2 + 1);
    const ray = new THREE.Raycaster(); ray.setFromCamera(mouse, e.camera);
    const meshes = []; groupRef.current.traverse((o) => { if (o.isMesh && o.userData.isFace) meshes.push(o); });
    const hits = ray.intersectObjects(meshes, true);
    if (!hits.length || !hits[0].face) return null;
    const it = hits[0];
    const n = it.face.normal.clone().applyMatrix3(new THREE.Matrix3().getNormalMatrix(it.object.matrixWorld)).normalize();
    const pos = it.object.geometry.attributes.position;
    const a = new THREE.Vector3().fromBufferAttribute(pos, it.face.a).applyMatrix4(it.object.matrixWorld);
    const b = new THREE.Vector3().fromBufferAttribute(pos, it.face.b).applyMatrix4(it.object.matrixWorld);
    const cc = new THREE.Vector3().fromBufferAttribute(pos, it.face.c).applyMatrix4(it.object.matrixWorld);
    const hg = new THREE.BufferGeometry().setFromPoints([a, b, cc]);
    const hl = new THREE.Mesh(hg, new THREE.MeshBasicMaterial({ color: 0xdb960d, transparent: true, opacity: 0.65, side: THREE.DoubleSide, depthTest: false }));
    hl.position.addScaledVector(n, (Math.max(dims?.x || 1, dims?.y || 1, dims?.z || 1)) * 0.003);
    return { normal: [n.x, n.y, n.z], highlight: hl };
  };
  const applyPreviewView = (dirArr, upArr) => {
    const e = engineRef.current, g = groupRef.current; if (!e || !g) return;
    const box = new THREE.Box3().setFromObject(g);
    const c = box.getCenter(new THREE.Vector3());
    const r = Math.max(...box.getSize(new THREE.Vector3()).toArray()) || 1;
    const dir = new THREE.Vector3(...dirArr).normalize();
    e.camera.up.set(...upArr);
    e.camera.position.copy(c).addScaledVector(dir, r * 2.4);
    e.controls.target.copy(c); e.camera.lookAt(c); e.controls.update();
  };
  const onFaceClick = (ev) => {
    if (!faceMode) return;
    const dp = downPos.current;
    if (dp && Math.hypot(ev.clientX - dp.x, ev.clientY - dp.y) > 6) return; // ignore drag (orbit)
    const f = pickFaceLocal(ev);
    if (!f) { setError("Klik op een vlak van het model"); return; }
    setError("");
    engineRef.current.scene.add(f.highlight); highlightRef.current.push(f.highlight);
    if (!frontN) {
      setFrontN(f.normal);
    } else {
      // build orthonormal basis from front + bottom normals
      const up = new THREE.Vector3(...f.normal).multiplyScalar(-1).normalize(); // up = -bottom
      let front = new THREE.Vector3(...frontN);
      front.sub(up.clone().multiplyScalar(front.dot(up)));
      if (front.lengthSq() < 1e-6) front = new THREE.Vector3(...frontN);
      front.normalize();
      const right = new THREE.Vector3().crossVectors(up, front).normalize();
      basisRef.current = { right: [right.x, right.y, right.z], up: [up.x, up.y, up.z], front: [front.x, front.y, front.z] };
      computeDims();
      setOriented(true); setFaceMode(false); setFrontN(null); clearHighlights();
      exitMeasure(); setMeasurements([]); // measurements are basis-relative
      applyPreviewView(basisRef.current.front, basisRef.current.up);
    }
  };
  const toggleFaceMode = () => { setError(""); setFrontN(null); clearHighlights(); exitMeasure(); setFaceMode((v) => !v); };
  const resetOrientation = () => {
    basisRef.current = { right: [1, 0, 0], up: [0, 1, 0], front: [0, 0, 1] };
    setOriented(false); setFrontN(null); setFaceMode(false); clearHighlights();
    exitMeasure(); setMeasurements([]); computeDims();
    applyPreviewView([0.8, 0.6, 1], [0, 1, 0]);
  };

  const loadFile = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["stl", "obj", "glb", "gltf"].includes(ext)) { setError("Niet-ondersteund formaat. Exporteer als STL of OBJ."); return; }
    const base = f.name.replace(/\.[^.]+$/, "");
    setError(""); setLoading(true);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        if (ext === "stl") addModel(new THREE.Mesh(new STLLoader().parse(reader.result), new THREE.MeshBasicMaterial()), base);
        else if (ext === "obj") addModel(new OBJLoader().parse(reader.result), base);
        else new GLTFLoader().parse(reader.result, "", (gltf) => addModel(gltf.scene, base), () => { setError("Kon GLB/GLTF niet laden."); setLoading(false); });
      } catch { setError("Kon het model niet laden. Probeer STL of OBJ."); setLoading(false); }
    };
    if (ext === "obj") reader.readAsText(f); else reader.readAsArrayBuffer(f);
  };
  const onUpload = (e) => { const f = (e.target.files || [])[0]; e.target.value = ""; if (f) loadFile(f); };
  const onDrop = (e) => { e.preventDefault(); const f = (e.dataTransfer.files || [])[0]; if (f) loadFile(f); };

  // Re-encode any image data URL to a clean PNG (jsPDF-safe) and set it as the logo.
  const loadLogoFromDataUrl = (dataUrl, { toastMsg, source } = {}) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas"); c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      setLogo({ url: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight, source });
      if (toastMsg) toast.success(toastMsg);
    };
    img.onerror = () => { if (toastMsg) toast.error("Kon het logo niet laden"); };
    img.src = dataUrl;
  };

  // Upload a PNG/JPG logo for the PDF title block; re-encode via canvas to a clean PNG.
  const onLogoUpload = (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f) return;
    const r = new FileReader();
    r.onload = () => loadLogoFromDataUrl(r.result, { toastMsg: "Logo toegevoegd", source: "upload" });
    r.readAsDataURL(f);
  };

  // Auto-load the Brand Kit logo so it appears by default (user can still override/remove).
  useEffect(() => {
    api.get("/brandkit").then((r) => {
      if (r.data?.logo) loadLogoFromDataUrl(r.data.logo, { source: "brandkit" });
    }).catch(() => {});
  }, []);

  // Rasterize the real KANN. wordmark (Cascadia Code bold italic) to a PNG for the PDF.
  const scaleLabel = (s) => (s < 1 ? `1:${Math.max(1, Math.round(1 / s))}` : `${Math.round(s)}:1`);


  const generate = async () => {
    if (!hasModel) return;
    setBusy(true); setError("");
    try {
      const M = 0.12 / 1.24; // model margin fraction inside each rendered image
      const front = renderOrtho("front");   // W x H
      const right = renderOrtho("right");   // D x H
      const top = renderOrtho("top");       // W x D
      const iso = renderOrtho("iso");

      const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      const PW = 297, PH = 210, mg = 8;
      const INK = [27, 27, 27], GOLD = [219, 150, 13], GREY = [120, 120, 120];
      // outer frame
      pdf.setDrawColor(...INK); pdf.setLineWidth(0.6); pdf.rect(mg, mg, PW - 2 * mg, PH - 2 * mg);
      pdf.setLineWidth(0.2);

      const tbW = 120, tbH = 42;
      const tbX = PW - mg - tbW, tbY = PH - mg - tbH;
      const viewsBottom = tbY; // views area above title block band
      const gx0 = mg, gy0 = mg, gW = PW - 2 * mg, gH = viewsBottom - mg;
      const colW = gW / 2, rowH = gH / 2;
      const panels = {
        front: { x: gx0, y: gy0, w: colW, h: rowH, label: "FRONT VIEW", dims: { w: front.wProj, h: front.hProj }, img: front },
        right: { x: gx0 + colW, y: gy0, w: colW, h: rowH, label: "RIGHT VIEW", dims: { w: right.wProj, h: right.hProj }, img: right },
        top: { x: gx0, y: gy0 + rowH, w: colW, h: rowH, label: "TOP VIEW", dims: { w: top.wProj, h: top.hProj }, img: top },
        iso: { x: gx0 + colW, y: gy0 + rowH, w: colW, h: rowH, label: "ISOMETRIC", dims: null, img: iso },
      };
      // panel dividers
      pdf.setDrawColor(...GREY); pdf.setLineWidth(0.15);
      pdf.line(gx0 + colW, gy0, gx0 + colW, PH - mg);
      pdf.line(gx0, gy0 + rowH, gx0 + gW, gy0 + rowH);

      // uniform scale for the 3 ortho views (fit into model-draw-area)
      const padL = 15, padB = 15, padT = 15, padR = 8; // room for dim lines + top label
      const ortho = [panels.front, panels.right, panels.top];
      let s = Infinity;
      ortho.forEach((p) => {
        const aw = p.w - padL - padR, ah = p.h - padT - padB;
        s = Math.min(s, aw / p.dims.w, ah / p.dims.h);
      });
      s = s * 0.98;

      const arrow = (x, y, dx, dy) => { // filled arrowhead at (x,y) pointing (dx,dy) unit
        const len = 2.2, wid = 0.9;
        const nx = -dy, ny = dx;
        pdf.triangle(x, y, x - dx * len + nx * wid, y - dy * len + ny * wid, x - dx * len - nx * wid, y - dy * len - ny * wid, "F");
      };

      const drawPanel = (p) => {
        pdf.setTextColor(...GREY); pdf.setFont("helvetica", "bold"); pdf.setFontSize(7);
        pdf.text(p.label, p.x + 3, p.y + 5);
        let imgW, imgH, cx, cy;
        if (p.dims) {
          const mw = s * p.dims.w, mh = s * p.dims.h;      // drawn model size
          imgW = mw / (1 - 2 * M); imgH = mh / (1 - 2 * M);
          cx = p.x + (padL - padR) / 2 + p.w / 2;
          cy = p.y + (padT - padB) / 2 + p.h / 2;
        } else {
          // iso: fit FULLY inside a reduced area — leaves room for the top label and a
          // clear gap above the title block at the bottom, so the model never clips.
          const im = p.img; const ar = im.wProj / im.hProj;
          const topPad = 9, botPad = 8, sidePad = 9;
          const aw = p.w - 2 * sidePad, ah = p.h - topPad - botPad;
          if (aw / ah > ar) { imgH = ah; imgW = ah * ar; } else { imgW = aw; imgH = aw / ar; }
          cx = p.x + p.w / 2;
          cy = p.y + topPad + ah / 2;
        }
        const ix = cx - imgW / 2, iy = cy - imgH / 2;
        pdf.addImage(p.img.url, "JPEG", ix, iy, imgW, imgH);
        if (!p.dims) return;
        const ml = ix + M * imgW, mr = ix + (1 - M) * imgW, mt = iy + M * imgH, mb = iy + (1 - M) * imgH;
        pdf.setDrawColor(...INK); pdf.setLineWidth(0.2); pdf.setTextColor(...INK); pdf.setFont("helvetica", "normal"); pdf.setFontSize(8);
        // width dim (below)
        const dy = mb + 8;
        pdf.line(ml, mb + 1.5, ml, dy + 1.5); pdf.line(mr, mb + 1.5, mr, dy + 1.5);
        pdf.line(ml, dy, mr, dy); arrow(ml, dy, 1, 0); arrow(mr, dy, -1, 0);
        const wt = fmtMM(p.dims.w); const wtw = pdf.getTextWidth(wt);
        pdf.setFillColor(255, 255, 255); pdf.rect((ml + mr) / 2 - wtw / 2 - 1, dy - 3.4, wtw + 2, 3.6, "F");
        pdf.text(wt, (ml + mr) / 2, dy - 1, { align: "center" });
        // height dim (left)
        const dx = ml - 8;
        pdf.line(ml - 1.5, mt, dx - 1.5, mt); pdf.line(ml - 1.5, mb, dx - 1.5, mb);
        pdf.line(dx, mt, dx, mb); arrow(dx, mt, 0, 1); arrow(dx, mb, 0, -1);
        const ht = fmtMM(p.dims.h); const htw = pdf.getTextWidth(ht);
        pdf.setFillColor(255, 255, 255); pdf.rect(dx - htw / 2 - 1, (mt + mb) / 2 - htw / 2 - 1, 3.6, htw + 2, "F");
        pdf.text(ht, dx - 1.2, (mt + mb) / 2, { align: "center", angle: 90 });
        // ---- manual measurements on this view ----
        const im = p.img;
        const fx = (u) => ix + (M + (u - im.uMin) / ((im.uMax - im.uMin) || 1) * (1 - 2 * M)) * imgW;
        const fy = (v) => iy + (M + (im.vMax - v) / ((im.vMax - im.vMin) || 1) * (1 - 2 * M)) * imgH;
        const icx = (ml + mr) / 2, icy = (mt + mb) / 2;
        measurements.filter((mz) => mz.view === im.name).forEach((mz) => {
          const A = { x: fx(mz.u1), y: fy(mz.v1) }, B = { x: fx(mz.u2), y: fy(mz.v2) };
          const ddx = B.x - A.x, ddy = B.y - A.y, len = Math.hypot(ddx, ddy) || 1;
          const ux = ddx / len, uy = ddy / len; let nx = -uy, ny = ux;
          const midx = (A.x + B.x) / 2, midy = (A.y + B.y) / 2;
          if ((nx * (midx - icx) + ny * (midy - icy)) < 0) { nx = -nx; ny = -ny; } // offset outward
          const off = 8;
          const A2 = { x: A.x + nx * off, y: A.y + ny * off }, B2 = { x: B.x + nx * off, y: B.y + ny * off };
          pdf.setDrawColor(...INK); pdf.setLineWidth(0.2); pdf.setTextColor(...INK); pdf.setFont("helvetica", "normal"); pdf.setFontSize(8);
          pdf.line(A.x, A.y, A2.x, A2.y); pdf.line(B.x, B.y, B2.x, B2.y); // extension lines
          pdf.line(A2.x, A2.y, B2.x, B2.y);                               // dimension line
          pdf.setFillColor(...INK); arrow(A2.x, A2.y, ux, uy); arrow(B2.x, B2.y, -ux, -uy);
          const mtxt = fmtMM(mz.dist), tw = pdf.getTextWidth(mtxt);
          const lx = (A2.x + B2.x) / 2, ly = (A2.y + B2.y) / 2;
          pdf.setFillColor(255, 255, 255);
          if (Math.abs(ux) >= Math.abs(uy)) { // horizontal-ish: text above line (like automatic width dim)
            pdf.rect(lx - tw / 2 - 1, ly - 3.4, tw + 2, 3.6, "F");
            pdf.text(mtxt, lx, ly - 1, { align: "center" });
          } else { // vertical-ish: rotated text (like automatic height dim)
            pdf.rect(lx - 1.8, ly - tw / 2 - 1, 3.6, tw + 2, "F");
            pdf.text(mtxt, lx - 0.2, ly, { align: "center", angle: 90 });
          }
        });
      };
      Object.values(panels).forEach(drawPanel);

      // ---- Title block (bottom-right) ----
      pdf.setDrawColor(...INK); pdf.setLineWidth(0.4); pdf.rect(tbX, tbY, tbW, tbH);
      // logo
      const headH = 13, padX = 4;
      if (logo) {
        const maxH = 6.5, maxW = tbW - 2 * padX, ar = (logo.w || 1) / (logo.h || 1);
        let lh = maxH, lw = maxH * ar;
        if (lw > maxW) { lw = maxW; lh = maxW / ar; }
        try { pdf.addImage(logo.url, "PNG", tbX + padX, tbY + 2, lw, lh); } catch (_) { /* logo optional: skip if the image can't be embedded */ }
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(5.5); pdf.setTextColor(...GREY);
        pdf.text("TECHNICAL DRAWING", tbX + padX, tbY + 11.2);
      } else {
        pdf.setFont("helvetica", "bolditalic"); pdf.setFontSize(16); pdf.setTextColor(...INK);
        pdf.text("KANN", tbX + padX, tbY + 8);
        const kw = pdf.getTextWidth("KANN");
        pdf.setTextColor(...GOLD); pdf.text(".", tbX + padX + kw + 0.5, tbY + 8);
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(5.5); pdf.setTextColor(...GREY);
        pdf.text("TECHNICAL DRAWING", tbX + padX, tbY + 11.2);
      }
      pdf.setDrawColor(...GREY); pdf.setLineWidth(0.15); pdf.line(tbX, tbY + headH, tbX + tbW, tbY + headH);

      // Compact data grid: wide LEFT column (Project/Part) + 2 columns RIGHT for short fields.
      const dataY = tbY + headH, dataH = tbH - headH;
      const leftW = tbW * 0.42, rightX = tbX + leftW, rCol = (tbW - leftW) / 2;
      const cellBox = (x, y, w, h, label, val, valSize) => {
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(5); pdf.setTextColor(...GREY);
        pdf.text(label.toUpperCase(), x + padX, y + 3.2);
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(valSize); pdf.setTextColor(...INK);
        pdf.text(String(val), x + padX, y + h - 2.6, { maxWidth: w - padX - 2 });
      };
      const leftRowH = dataH / 2;
      cellBox(tbX, dataY, leftW, leftRowH, "Project", meta.project || "—", 8);
      cellBox(tbX, dataY + leftRowH, leftW, leftRowH, "Part", meta.part || "—", 8);
      const rRowH = dataH / 3;
      const rightRows = [
        ["Drawing no.", meta.drawingNo || "—", "Date", meta.date || "—"],
        ["Scale", scaleLabel(s), "Units", "mm"],
        ["Format", "A4 · landscape", "Projection", "3-view + iso"],
      ];
      rightRows.forEach((r, i) => {
        const y = dataY + i * rRowH;
        cellBox(rightX, y, rCol, rRowH, r[0], r[1], 7.5);
        cellBox(rightX + rCol, y, rCol, rRowH, r[2], r[3], 7.5);
      });
      // grid lines
      pdf.setDrawColor(...GREY); pdf.setLineWidth(0.1);
      pdf.line(rightX, dataY, rightX, tbY + tbH);                 // left | right divider
      pdf.line(rightX + rCol, dataY, rightX + rCol, tbY + tbH);   // right inner divider
      pdf.line(tbX, dataY + leftRowH, rightX, dataY + leftRowH);  // left mid horizontal
      pdf.line(rightX, dataY + rRowH, tbX + tbW, dataY + rRowH);
      pdf.line(rightX, dataY + 2 * rRowH, tbX + tbW, dataY + 2 * rRowH);

      // on-brand dot grid in the empty area left of the title block
      pdf.setFillColor(214, 214, 214);
      const dotStep = 4, dotR = 0.32;
      const dgX1 = tbX - 1.5, dgY0 = tbY, dgY1 = tbY + tbH - 9.5;
      for (let y = dgY0; y <= dgY1; y += dotStep) {
        for (let x = mg + 3; x <= dgX1; x += dotStep) pdf.circle(x, y, dotR, "F");
      }

      // confidentiality notice (bottom-left, beside title block)
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(6.5); pdf.setTextColor(...INK);
      pdf.text("CONFIDENTIAL", mg + 3, tbY + tbH - 6.5);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(6); pdf.setTextColor(...GREY);
      pdf.text("Property of KANN. Not to be copied, distributed or disclosed without prior written consent.", mg + 3, tbY + tbH - 3);

      const blob = pdf.output("blob");
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      toast.success("Voorbeeld gegenereerd — bekijk het hieronder en klik Download");
    } catch (err) {
      console.error(err); setError("Kon de tekening niet genereren."); toast.error("Genereren mislukt");
    } finally { setBusy(false); }
  };

  const field = (key, label, ph = "") => (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-kann-text-sec font-mono">{label}</label>
      <input value={meta[key]} onChange={(e) => setMeta((m) => ({ ...m, [key]: e.target.value }))} placeholder={ph}
        data-testid={`td-${key}`} className="mt-1 w-full bg-kann-base border border-kann-border/50 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2 text-sm outline-none" />
    </div>
  );

  return (
    <div className="flex bg-kann-base min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 p-4 md:p-8 lg:p-10 pt-16 md:pt-8 relative">
        <div className="absolute inset-0 -z-10 bg-dots overflow-hidden"><div className="glow-orb" style={{ width: 620, height: 620, top: -140, left: "55%" }} /></div>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-kann-gold font-mono">3D → Tekening</div>
            <h1 className="text-4xl font-medium text-kann-text-pri mt-2">Technische Tekening</h1>
            <p className="font-light text-kann-text-sec mt-2 max-w-xl">Upload een 3D-model (STL/OBJ) en genereer met één klik een on-brand technische tekening met 3 aanzichten, iso-hoek en automatische buitenafmetingen (mm) als A4-PDF.</p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Preview */}
          <div className="lg:col-span-2 bg-kann-surface border border-kann-border/30 rounded-lg overflow-hidden flex flex-col" style={{ minHeight: 460 }}>
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-kann-border/20">
              <Move3d className="w-4 h-4 text-kann-gold" />
              <span className="text-sm font-medium text-kann-text-pri">Voorbeeld</span>
              {hasModel && (
                <div className="ml-auto flex items-center gap-2">
                  <button onClick={toggleFaceMode} data-testid="td-face-mode" title="Selecteer voor- en ondervlak om het model recht te zetten"
                    className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 border transition-colors ${faceMode ? "bg-kann-gold text-kann-ink border-kann-gold" : oriented ? "bg-kann-gold/15 border-kann-gold text-kann-text-pri" : "border-kann-border/50 text-kann-text-sec hover:bg-kann-surface-active"}`}>
                    <SquareDashedMousePointer className="w-3.5 h-3.5" /> {oriented ? "Uitgelijnd" : "Vlak"}
                  </button>
                  {oriented && <button onClick={resetOrientation} data-testid="td-reset-orientation" className="text-xs text-kann-text-sec hover:text-red-500">Herstel</button>}
                  <button onClick={fitCamera} data-testid="td-fit" className="text-xs text-kann-text-sec hover:text-kann-gold flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" /> Passend</button>
                  <button onClick={() => setNamedView("iso")} data-testid="td-view-home" title="Iso-hoek" className="text-kann-text-sec hover:text-kann-gold p-1"><Home className="w-3.5 h-3.5" /></button>
                  <button onClick={() => tiltView(engineRef.current, 22.5)} data-testid="td-tilt-up" title="Kantel omhoog" className="text-kann-text-sec hover:text-kann-gold p-1"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => tiltView(engineRef.current, -22.5)} data-testid="td-tilt-down" title="Kantel omlaag" className="text-kann-text-sec hover:text-kann-gold p-1"><ChevronDown className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
            <div className="relative flex-1" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
              <div ref={mountRef}
                onClick={(e) => { if (measureView) onMeasureClick(e); else if (faceMode) onFaceClick(e); }}
                onPointerDown={(e) => (downPos.current = { x: e.clientX, y: e.clientY })}
                onPointerMove={onMeasureMove}
                onPointerLeave={onMeasureLeave}
                className="w-full h-full" data-testid="td-canvas"
                style={{ backgroundImage: "radial-gradient(#E5E5E5 1px, transparent 1px)", backgroundSize: "20px 20px", background: "#ffffff", cursor: (faceMode || measureView) ? "crosshair" : "default" }} />
              {measureView && mountRef.current && (() => {
                const rect = mountRef.current.getBoundingClientRect();
                const c = cursorRef.current;
                const cur = c ? { x: (c.snap ? c.snap.x : c.clientX) - rect.left, y: (c.snap ? c.snap.y : c.clientY) - rect.top } : null;
                const pp = pendingPt ? uvToScreen(pendingPt.u, pendingPt.v) : null;
                return (
                  <svg className="absolute inset-0 pointer-events-none" width="100%" height="100%">
                    {measurements.filter((m) => m.view === measureView).map((m) => {
                      const A = uvToScreen(m.u1, m.v1), B = uvToScreen(m.u2, m.v2);
                      return (<g key={m.id}>
                        <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke="#1b1b1b" strokeWidth="1.2" />
                        <circle cx={A.x} cy={A.y} r="2.5" fill="#1b1b1b" /><circle cx={B.x} cy={B.y} r="2.5" fill="#1b1b1b" />
                        <rect x={(A.x + B.x) / 2 - 16} y={(A.y + B.y) / 2 - 8} width="32" height="15" rx="2" fill="white" stroke="#1b1b1b" strokeWidth="0.5" />
                        <text x={(A.x + B.x) / 2} y={(A.y + B.y) / 2 + 3} textAnchor="middle" fontSize="10" fontWeight="700" fill="#1b1b1b">{fmtMM(m.dist)}</text>
                      </g>);
                    })}
                    {pp && cur && <line x1={pp.x} y1={pp.y} x2={cur.x} y2={cur.y} stroke="#DB960D" strokeWidth="1.2" strokeDasharray="4 3" />}
                    {pp && <circle cx={pp.x} cy={pp.y} r="4" fill="none" stroke="#DB960D" strokeWidth="2" />}
                    {cur && (c.snap?.kind === "center" ? (
                      <g>
                        <circle cx={cur.x} cy={cur.y} r="6" fill="none" stroke="#DB960D" strokeWidth="2" />
                        <line x1={cur.x - 9} y1={cur.y} x2={cur.x + 9} y2={cur.y} stroke="#DB960D" strokeWidth="2" />
                        <line x1={cur.x} y1={cur.y - 9} x2={cur.x} y2={cur.y + 9} stroke="#DB960D" strokeWidth="2" />
                      </g>
                    ) : (
                      <circle cx={cur.x} cy={cur.y} r={c.snap ? 5 : 3} fill={c.snap ? "none" : "#DB960D"} stroke="#DB960D" strokeWidth="2" />
                    ))}
                  </svg>
                );
              })()}
              {!hasModel && !loading && (
                <button onClick={() => fileRef.current?.click()} data-testid="td-dropzone" className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-kann-text-sec hover:text-kann-text-pri transition-colors">
                  <div className="w-16 h-16 rounded-full bg-kann-base border border-kann-border/40 flex items-center justify-center"><Upload className="w-7 h-7" /></div>
                  <div className="text-sm font-medium">Sleep een STL/OBJ hierheen of klik om te uploaden</div>
                  <div className="text-xs">1 modeleenheid = 1 mm</div>
                </button>
              )}
              {loading && <div className="absolute inset-0 flex items-center justify-center bg-white/60"><Loader2 className="w-7 h-7 animate-spin text-kann-gold" /></div>}
              {faceMode && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-kann-gold text-kann-ink text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-2 z-30" data-testid="td-face-hint">
                  {frontN ? "Stap 2/2 — klik het ONDERVLAK (onderkant) aan" : "Stap 1/2 — klik het VOORVLAK aan"}
                  <button onClick={() => { clearHighlights(); setFrontN(null); setFaceMode(false); }} data-testid="td-face-cancel" className="hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
              {measureView && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-kann-gold text-kann-ink text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-2 z-30" data-testid="td-measure-hint">
                  {pendingPt ? "Klik het TWEEDE punt (snapt naar hoekpunten en cirkelmiddens)" : `Maat op ${measureView === "front" ? "vooraanzicht" : measureView === "right" ? "rechts" : "bovenaanzicht"} — klik het EERSTE punt`}
                  <button onClick={exitMeasure} data-testid="td-measure-done" className="hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
              {measureView && cursorRef.current && (
                <canvas ref={magRef} width={MAG_PX} height={MAG_PX} data-testid="td-magnifier"
                  className="fixed pointer-events-none rounded-full border-2 border-kann-gold shadow-2xl z-50"
                  style={{ width: MAG_CSS, height: MAG_CSS, left: Math.min(cursorRef.current.clientX + 22, window.innerWidth - MAG_CSS - 8), top: cursorRef.current.clientY - MAG_CSS - 22 < 8 ? cursorRef.current.clientY + 22 : cursorRef.current.clientY - MAG_CSS - 22, background: "#fff" }} />
              )}
              {hasModel && !faceMode && !measureView && <div className="absolute bottom-3 left-3 bg-black/70 text-white text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none"><Move3d className="w-3.5 h-3.5" /> Sleep om te draaien · scroll om te zoomen{oriented ? " · uitgelijnd op gekozen vlak" : ""}</div>}
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-5">
            <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-4">
              <button onClick={() => fileRef.current?.click()} data-testid="td-upload-btn" className="w-full bg-kann-ink text-white text-sm font-semibold rounded-full px-4 py-2.5 hover:bg-black flex items-center justify-center gap-2"><Upload className="w-4 h-4" /> Model uploaden</button>
              <input ref={fileRef} type="file" accept=".stl,.obj,.glb,.gltf" className="hidden" onChange={onUpload} data-testid="td-file-input" />
              {dims && (
                <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-testid="td-dims">
                  {[["Breedte", dims.x], ["Hoogte", dims.y], ["Diepte", dims.z]].map(([l, v]) => (
                    <div key={l} className="bg-kann-base border border-kann-border/40 rounded-md py-2">
                      <div className="text-[10px] uppercase tracking-wider text-kann-text-sec font-mono">{l}</div>
                      <div className="text-sm font-semibold text-kann-text-pri tabular-nums">{fmtMM(v)}<span className="text-[10px] text-kann-text-sec ml-0.5">mm</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-4 space-y-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-kann-text-sec font-mono">Titelblok</div>
              {field("project", "Project", "bv. Philips Hue houder")}
              {field("part", "Onderdeel", "bv. Basisplaat")}
              {field("drawingNo", "Tekeningnr.", "bv. KANN-001")}
              {field("date", "Datum")}
              <div>
                <label className="text-[11px] uppercase tracking-wider text-kann-text-sec font-mono">Logo (PNG/JPG)</label>
                <input ref={logoRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onLogoUpload} data-testid="td-logo-input" />
                <div className="mt-1 flex items-center gap-2">
                  {logo ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="h-8 px-2 flex items-center bg-white border border-kann-border/40 rounded-md">
                        <img src={logo.url} alt="logo" className="max-h-6 max-w-[110px] object-contain" data-testid="td-logo-thumb" />
                      </div>
                      <button onClick={() => logoRef.current?.click()} data-testid="td-logo-change" className="text-xs text-kann-gold hover:underline">Wijzig</button>
                      <button onClick={() => setLogo(null)} data-testid="td-logo-remove" className="text-xs text-kann-text-sec hover:text-red-500">Verwijder</button>
                    </div>
                  ) : (
                    <button onClick={() => logoRef.current?.click()} data-testid="td-logo-upload" className="w-full text-sm text-kann-text-pri border border-dashed border-kann-border/60 rounded-md py-2 hover:bg-kann-base flex items-center justify-center gap-2"><Upload className="w-3.5 h-3.5" /> Logo uploaden</button>
                  )}
                </div>
                <p className="mt-1 text-[10px] text-kann-text-sec">{logo?.source === "brandkit" ? "Automatisch geladen uit je Brand Kit. Upload een ander logo om te overschrijven." : "Laat leeg voor het standaard \"KANN.\"-woordmerk."}</p>
              </div>
            </div>

            {hasModel && (
              <div className="bg-kann-surface border border-kann-border/30 rounded-lg p-4 space-y-3" data-testid="td-measure-card">
                <div className="flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-kann-gold" />
                  <div className="text-[11px] uppercase tracking-[0.2em] text-kann-text-sec font-mono">Eigen maten</div>
                </div>
                <p className="text-xs text-kann-text-sec">Kies een aanzicht, klik dan 2 punten op het model (snapt naar hoekpunten én cirkel-/gatmiddens).</p>
                <div className="grid grid-cols-3 gap-2">
                  {[["front", "Voor"], ["top", "Boven"], ["right", "Rechts"]].map(([v, l]) => (
                    <button key={v} onClick={() => (measureView === v ? exitMeasure() : enterMeasure(v))} data-testid={`td-measure-${v}`}
                      className={`text-xs font-medium rounded-md py-2 border transition-colors ${measureView === v ? "bg-kann-gold text-kann-ink border-kann-gold" : "border-kann-border/50 text-kann-text-pri hover:bg-kann-base"}`}>{l}</button>
                  ))}
                </div>
                {measurements.length > 0 && (
                  <div className="space-y-1.5 pt-1" data-testid="td-measure-list">
                    {measurements.map((m) => (
                      <div key={m.id} className="flex items-center justify-between bg-kann-base border border-kann-border/40 rounded-md px-2.5 py-1.5 text-xs">
                        <span className="text-kann-text-sec">{m.view === "front" ? "Voor" : m.view === "right" ? "Rechts" : "Boven"}</span>
                        <span className="font-semibold text-kann-text-pri tabular-nums">{fmtMM(m.dist)} mm</span>
                        <button onClick={() => removeMeasurement(m.id)} data-testid={`td-measure-del-${m.id}`} className="text-kann-text-sec hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button onClick={generate} disabled={!hasModel || busy} data-testid="td-generate-btn" className="w-full bg-kann-gold text-kann-ink font-semibold rounded-full px-4 py-3 text-sm hover:bg-yellow-500 transition-colors flex items-center justify-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ruler className="w-4 h-4" />} Genereer voorbeeld (PDF)
            </button>
            {error && <div className="text-xs text-red-500" data-testid="td-error">{error}</div>}
          </div>
        </div>

        {pdfUrl && (
          <div className="mt-8 bg-kann-surface border border-kann-border/30 rounded-lg overflow-hidden" data-testid="td-pdf-preview">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-kann-border/20">
              <Ruler className="w-4 h-4 text-kann-gold" />
              <span className="text-sm font-medium text-kann-text-pri">PDF-voorbeeld</span>
              <a href={pdfUrl} download={`${(meta.part || meta.project || "technische-tekening").replace(/\s+/g, "-").toLowerCase()}.pdf`} data-testid="td-download-btn" className="ml-auto bg-kann-ink text-white text-xs font-semibold rounded-full px-3.5 py-1.5 hover:bg-black flex items-center gap-1.5"><FileDown className="w-3.5 h-3.5" /> Download PDF</a>
            </div>
            <iframe title="pdf" src={pdfUrl} className="w-full" style={{ height: 620, border: "none", background: "#525659" }} />
          </div>
        )}
      </main>
    </div>
  );
}
